// PrintingHoldOrdersPage.js
import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPrintingHoldOrders, updateOrderStatus, deleteOrder, fetchPrintingHoldOrdersSummary, checkDuplicatePhoneCount, markOrdersSentToFincartBulk } from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { formatPhoneWithCaseStyle, teePlusPackedItemData } from "../../utils/orderLineDisplay";
import { getStoredFincartOrderId, resolveOrderFincartPresence } from "../../api/FincartAPIs";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { downloadCustomFile } from "../../api/FileAPIs";
import { EGYPTIAN_GOVERNORATES } from "../../constants/governorates";
import EditOrderModal from "./EditOrderModal";
import { copyOrderToTeePlus } from "../../utils/productionSource";
import {
  ChevronRight,
  ChevronLeft,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  Copy,
  Download,
  Link as LinkIcon,
  Edit,
  Trash2,
  Archive,
  Layers,
  Filter,
  Search,
  Loader,
  Printer,
  Building2,
  CheckSquare,
  CheckCircle,
  AlertTriangle,
  Clock,
  Truck,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const HoldReasonDialog = ({ isOpen, onClose, onConfirm, orderId }) => {
  const OTHER_VALUE = "__OTHER__";
  const options = [
    { value: "ديبوزت , رفض قبل كده", label: "ديبوزت , رفض قبل كده" },
    { value: "عنوان", label: "عنوان" },
    { value: "هل هيدمج", label: "هل هيدمج" },
    { value: "الصوره مش واضحه يبعت واحده تانيه", label: "الصوره مش واضحه يبعت واحده تانيه" },
    { value: "ديبوزت", label: "ديبوزت" },
    { value: OTHER_VALUE, label: "حاجه تانيه" },
  ];

  const [selected, setSelected] = useState(options[0]?.value || "");
  const [customReason, setCustomReason] = useState("");
  const customRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(options[0]?.value || "");
    setCustomReason("");
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && selected === OTHER_VALUE && customRef.current) {
      customRef.current.focus();
    }
  }, [isOpen, selected]);

  if (!isOpen) return null;

  const finalReason = selected === OTHER_VALUE ? customReason.trim() : selected;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" dir="rtl">
        <h3 className="text-xl font-bold mb-2 text-gray-800">سبب التعليق (Hold)</h3>
        <p className="text-gray-600 mb-4">
          اختر سبب وضع الطلب #{orderId} على الانتظار:
        </p>

        <div className="space-y-3">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full p-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 bg-white"
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {selected === OTHER_VALUE && (
            <input
              ref={customRef}
              type="text"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              className="w-full p-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200"
              placeholder="اكتب السبب..."
            />
          )}
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={() => {
              onConfirm(finalReason);
              onClose();
            }}
            className="flex-1 bg-yellow-600 text-white py-2 rounded-xl hover:bg-yellow-700 transition"
            disabled={!finalReason}
          >
            تأكيد
          </button>
          <button
            onClick={() => onClose()}
            className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-xl hover:bg-gray-400 transition"
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
};

const PrintingHoldOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [designFilter, setDesignFilter] = useState("all"); // "all" | "custom" | "normal"
  /** all | clean | dup */
  const [dupFilter, setDupFilter] = useState("all");
  /** all | sent | notSent */
  const [fincartFilter, setFincartFilter] = useState("all");
  const [governorateFilter, setGovernorateFilter] = useState("all");
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState(new Set());
  const [isMarkingGirabatcom, setIsMarkingGirabatcom] = useState(false);
  const [showHoldDialog, setShowHoldDialog] = useState(false);
  const [pendingHoldOrder, setPendingHoldOrder] = useState(null);

  const { data: printingHoldOrdersSummary, isLoading: isLoadingSummary } = useQuery({
    queryKey: ["printingHoldOrdersSummary"],
    queryFn: fetchPrintingHoldOrdersSummary,
    enabled: canViewOrders(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0, totalProductsSold: 0 },
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const isLoading = isLoadingSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {
        // toast.success("تم النسخ بنجاح!");
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        // toast.success("تم النسخ بنجاح!");
      } else {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: design.code.startsWith("CUSTOM") 
              ? `${API_BASE_URL_V2}${design.driveLink}` 
              : design.driveLink || "",
            code: design.code || "None",
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "غير معروف",
          state: order.address?.state || "غير معروف",
          city: order.address?.city || "",
          phoneNumber: order.phoneNumber || "غير معروف",
          phoneNumber2: order.phoneNumber2 || "غير معروف",
          name: order.name || "غير معروف",
          note: order.note || "",
          status: order.status || "غير معروف",
          sentToFincart: !!order.sentToFincart || !!order.shippingOrderId || !!getStoredFincartOrderId(order.orderId),
          shippingOrderId: order.shippingOrderId || null,
          fincartOrderId: order.shippingOrderId || getStoredFincartOrderId(order.orderId) || null,
          dupSkipped: false,
          alreadyInFincart: !!(order.sentToFincart || order.shippingOrderId || getStoredFincartOrderId(order.orderId)),
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    setHasMore(true);
    try {
      const data = await fetchPrintingHoldOrders(0, pageSize);
      setHasMore(data.length === pageSize);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchPrintingHoldOrders(currentPage, pageSize);
      setHasMore(data.length === pageSize);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setIsOrdersLoading(true);
    setHasMore(true);
    try {
      const data = await fetchPrintingHoldOrders(0, newSize);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
      setHasMore(data.length === newSize);
    } catch (err) {
      console.error("Error changing page size:", err);
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const openHoldDialog = (orderId) => {
    setPendingHoldOrder(orderId);
    setShowHoldDialog(true);
  };

  const handleHoldReasonConfirm = async (reason) => {
    if (!canAct) return;
    const finalReason = (reason || "").trim();
    if (!finalReason) {
      toast.error("يجب اختيار/كتابة سبب");
      return;
    }

    const confirmUpdate = window.confirm(`هل أنت متأكد من تحديث الحالة إلى Hold؟\n\nالسبب: "${finalReason}"`);
    if (!confirmUpdate) return;

    try {
      await updateOrderStatus(pendingHoldOrder, "Hold", finalReason);
      setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== pendingHoldOrder));
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        next.delete(pendingHoldOrder);
        return next;
      });
      toast.success("تم تحديث الطلب إلى Hold");
    } catch (err) {
      toast.error("فشل في تحديث الطلب");
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!canAct) return;
    let reason = "no reason (normal order)";

    if (newStatus === "Hold") {
      openHoldDialog(orderId);
      return;
    }

    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
        setSelectedOrderNumbers((prev) => {
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        toast.success("تم تحديث الطلب إلى قديم!");
      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    if (newStatus === "Incomplete") {
      const note = window.prompt(
        "ملاحظة Incomplete (اختياري) — السبب أو نوع الجراب الناقص:\nاتركه فاضي لو مش عايز تكتب حاجة"
      );
      if (note === null) return;
      reason = note.trim() || "no reason (normal order)";
    }

    if (newStatus === "Girabatcom") {
      const order = loadedOrders.find((o) => o.orderNumber === orderId);
      if (!order) return;

      toast.info("جاري فحص Fincart والتكرار...", {
        autoClose: false,
        toastId: "girabatcom-dup-check-single",
      });

      let fincartHit = null;
      try {
        const { found } = await checkOrdersOnFincart([order]);
        fincartHit = found[0] || null;
      } catch (err) {
        console.error(`فشل فحص Fincart #${order.orderNumber}:`, err);
      }

      let dupCount = 0;
      try {
        dupCount = await checkDuplicatePhoneCount(
          order.orderNumber,
          order.phoneNumber,
          order.phoneNumber2 || ""
        );
      } catch (err) {
        console.error(`فشل فحص التكرار #${order.orderNumber}:`, err);
        toast.dismiss("girabatcom-dup-check-single");
        toast.warning("فشل فحص التكرار — اعتبره متكرر للمراجعة");
        dupCount = 1;
      }
      toast.dismiss("girabatcom-dup-check-single");

      if (fincartHit) {
        await persistDetectedFincart([fincartHit]);
        const agreeFincart = window.confirm(
          `تحذير: الطلب ده موجود بالفعل على Fincart!\n\nالطلب #${order.orderNumber} — ${order.name}` +
            (fincartHit.status ? `\nحالة Fincart: ${fincartHit.status}` : "") +
            `\n\nمش هيتبعت تاني لـ Fincart.\nهل توافق على التحويل لـ Girabatcom؟`
        );
        if (!agreeFincart) {
          markAlreadyInFincart([order], {
            byId: {
              [order.orderNumber]: { fincartOrderId: fincartHit.fincartOrderId },
            },
          });
          toast.info("تم إلغاء التحويل لأن الأوردر موجود على Fincart");
          return;
        }
      }

      if (dupCount > 0) {
        const agreeDuplicate = window.confirm(
          `تحذير: هذا العميل لديه ${dupCount} طلب(ات) سابقة بنفس الرقم!\n\nالطلب #${order.orderNumber} — ${order.name}\nالرقم: ${order.phoneNumber}\n\nهل توافق على التحويل لـ Girabatcom؟`
        );
        if (!agreeDuplicate) {
          markDupSkipped([order]);
          toast.info("تم إلغاء التحويل بسبب تكرار الرقم");
          return;
        }
      }

      const confirmGirabatcom = window.confirm(
        `تأكيد تحويل الطلب #${order.orderNumber} إلى Girabatcom؟\n\nسيختفي من الصفحة فورًا.`
      );
      if (!confirmGirabatcom) return;

      try {
        await updateOrderStatus(
          orderId,
          "Girabatcom",
          "تم التحويل لـ Girabatcom من Printing Hold"
        );
        setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
        setSelectedOrderNumbers((prev) => {
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        toast.success("تم تحديث الطلب إلى Girabatcom بنجاح");
      } catch (err) {
        toast.error("فشل التحديث: " + err.message);
      }
      return;
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const toggleSelectOrder = (orderNumber) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const setSelectAllVisible = (checked, visibleOrders) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (checked) {
        visibleOrders.forEach((o) => next.add(o.orderNumber));
      } else {
        visibleOrders.forEach((o) => next.delete(o.orderNumber));
      }
      return next;
    });
  };

  const markDupSkipped = (orders) => {
    if (!orders?.length) return;
    const nums = new Set(orders.map((o) => o.orderNumber));
    setLoadedOrders((prev) =>
      prev.map((o) =>
        nums.has(o.orderNumber) ? { ...o, dupSkipped: true } : o
      )
    );
  };

  const markAlreadyInFincart = (orders, extras = {}) => {
    if (!orders?.length) return;
    const byId = extras.byId || {};
    const nums = new Set(orders.map((o) => o.orderNumber));
    setLoadedOrders((prev) =>
      prev.map((o) => {
        if (!nums.has(o.orderNumber)) return o;
        const extra = byId[o.orderNumber] || {};
        return {
          ...o,
          alreadyInFincart: true,
          sentToFincart: true,
          fincartOrderId: extra.fincartOrderId || o.fincartOrderId,
          shippingOrderId: extra.fincartOrderId || o.shippingOrderId,
        };
      })
    );
  };

  const persistDetectedFincart = async (hits) => {
    if (!hits?.length) return;
    const items = hits
      .filter((hit) => hit.fincartOrderId)
      .map((hit) => ({
        orderId: hit.order.orderNumber,
        shippingOrderId: hit.fincartOrderId,
      }));
    try {
      await markOrdersSentToFincartBulk(
        hits.map((hit) => hit.order.orderNumber),
        true,
        items
      );
    } catch (err) {
      console.error("فشل حفظ علامة Fincart:", err);
    }
  };

  const checkOrdersOnFincart = async (orders) => {
    const found = [];
    const remaining = [];

    for (const order of orders) {
      try {
        const presence = await resolveOrderFincartPresence(order);
        if (presence.found) {
          found.push({
            order,
            fincartOrderId: presence.fincartOrderId || null,
            status: presence.status || "",
            source: presence.source,
          });
        } else {
          remaining.push(order);
        }
      } catch (err) {
        console.error(`فشل فحص Fincart #${order.orderNumber}:`, err);
        toast.warning(
          `فشل فحص Fincart لـ #${order.orderNumber} — اعتبره موجود للمراجعة`
        );
        found.push({
          order,
          fincartOrderId: getStoredFincartOrderId(order.orderNumber) || null,
          status: "",
          source: "error",
        });
      }
    }

    return { found, remaining };
  };

  const convertOrdersToGirabatcom = async (orders) => {
    let successCount = 0;
    let failedCount = 0;
    const succeededIds = [];
    const failedOrders = [];

    for (const order of orders) {
      try {
        await updateOrderStatus(
          order.orderNumber,
          "Girabatcom",
          "تم التحويل لـ Girabatcom من Printing Hold"
        );
        succeededIds.push(order.orderNumber);
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
        failedOrders.push(order);
      }
    }

    if (succeededIds.length > 0) {
      setLoadedOrders((prev) =>
        prev.filter((order) => !succeededIds.includes(order.orderNumber))
      );
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => next.delete(id));
        return next;
      });
    }

    return { successCount, failedCount, failedOrders };
  };

  const handleMarkSelectedAsGirabatcom = async () => {
    if (!canAct) return;

    const selected = loadedOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    setIsMarkingGirabatcom(true);

    toast.info("جاري فحص Fincart والتكرار قبل التحويل...", {
      autoClose: false,
      toastId: "girabatcom-dup-check",
    });

    const { found: fincartHits, remaining } = await checkOrdersOnFincart(selected);

    const cleanOrders = [];
    const duplicateOrders = [];

    for (const order of remaining) {
      try {
        const count = await checkDuplicatePhoneCount(
          order.orderNumber,
          order.phoneNumber,
          order.phoneNumber2 || ""
        );
        if (count > 0) {
          duplicateOrders.push({ order, count });
        } else {
          cleanOrders.push(order);
        }
      } catch (err) {
        console.error(`فشل فحص التكرار #${order.orderNumber}:`, err);
        toast.warning(
          `فشل فحص التكرار لـ #${order.orderNumber} — اعتبره متكرر للمراجعة`
        );
        duplicateOrders.push({ order, count: "?" });
      }
    }

    toast.dismiss("girabatcom-dup-check");

    const fincartById = Object.fromEntries(
      fincartHits.map((hit) => [
        hit.order.orderNumber,
        { fincartOrderId: hit.fincartOrderId },
      ])
    );

    const skipFincartHits = async () => {
      await persistDetectedFincart(fincartHits);
      markAlreadyInFincart(
        fincartHits.map((hit) => hit.order),
        { byId: fincartById }
      );
    };

    // Single order path
    if (selected.length === 1) {
      const only = selected[0];
      const fincartHit = fincartHits[0];
      const dup = duplicateOrders[0];

      if (fincartHit) {
        await persistDetectedFincart([fincartHit]);
        const agreeFincart = window.confirm(
          `تحذير: الطلب ده موجود بالفعل على Fincart!\n\nالطلب #${only.orderNumber} — ${only.name}` +
            (fincartHit.status ? `\nحالة Fincart: ${fincartHit.status}` : "") +
            `\n\nمش هيتبعت تاني لـ Fincart.\nهل توافق على التحويل لـ Girabatcom؟`
        );
        if (!agreeFincart) {
          markAlreadyInFincart([only], { byId: fincartById });
          setIsMarkingGirabatcom(false);
          toast.info("تم إلغاء التحويل لأن الأوردر موجود على Fincart");
          return;
        }
      }

      if (dup) {
        const agreeDuplicate = window.confirm(
          `تحذير: هذا العميل لديه ${dup.count} طلب(ات) سابقة بنفس الرقم!\n\nالطلب #${only.orderNumber} — ${only.name}\nالرقم: ${only.phoneNumber}\n\nهل توافق على التحويل لـ Girabatcom؟`
        );
        if (!agreeDuplicate) {
          markDupSkipped([only]);
          setIsMarkingGirabatcom(false);
          toast.info("تم إلغاء التحويل بسبب تكرار الرقم");
          return;
        }
      }

      const confirmSend = window.confirm(
        `هل تريد تحويل الطلب #${only.orderNumber} إلى "Girabatcom"؟\n\nسيختفي من الصفحة فورًا.`
      );
      if (!confirmSend) {
        setIsMarkingGirabatcom(false);
        return;
      }

      toast.info("جاري تحويل المحدد إلى Girabatcom... لا تغلق الصفحة", {
        autoClose: false,
        toastId: "marking-girabatcom",
      });

      const { successCount, failedCount } = await convertOrdersToGirabatcom([only]);

      toast.dismiss("marking-girabatcom");
      setIsMarkingGirabatcom(false);

      if (successCount === 0) {
        toast.error(
          `فشل تحويل الطلب إلى Girabatcom` +
            (failedCount > 0 ? ` (${failedCount} طلب)` : "")
        );
        return;
      }

      toast.success(`تم تحويل الطلب إلى Girabatcom بنجاح!`);
      return;
    }

    let totalSuccess = 0;
    let totalFailed = 0;

    // Clean (non-duplicate, not already on Fincart) first
    if (cleanOrders.length > 0) {
      const confirmClean = window.confirm(
        `تحويل ${cleanOrders.length} طلب(ات) إلى Girabatcom؟` +
          (duplicateOrders.length > 0
            ? `\n\nسيتم تخطي ${duplicateOrders.length} طلب(ات) متكررة مؤقتاً — هسألك عنهم بعد التحويل`
            : "") +
          (fincartHits.length > 0
            ? `\n\nسيتم تخطي ${fincartHits.length} طلب(ات) موجودين على Fincart مؤقتاً — هسألك عنهم بعد التحويل`
            : "") +
          `\n\nستختفي من الصفحة فورًا.`
      );
      if (!confirmClean) {
        setIsMarkingGirabatcom(false);
        toast.info("تم إلغاء التحويل");
        return;
      }

      toast.info("جاري تحويل المحدد إلى Girabatcom... لا تغلق الصفحة", {
        autoClose: false,
        toastId: "marking-girabatcom",
      });

      const { successCount, failedCount } = await convertOrdersToGirabatcom(cleanOrders);
      totalSuccess += successCount;
      totalFailed += failedCount;
      toast.dismiss("marking-girabatcom");
    }

    if (duplicateOrders.length > 0) {
      const dupList = duplicateOrders
        .slice(0, 15)
        .map(
          ({ order, count }) =>
            `#${order.orderNumber} — ${order.name} (${count} سابق)`
        )
        .join("\n");

      const sendDuplicates = window.confirm(
        `فيه ${duplicateOrders.length} طلب(ات) متكررة متحولتش لـ Girabatcom.\n\n${dupList}` +
          (duplicateOrders.length > 15 ? "\n..." : "") +
          `\n\nهل عايز تحولهم برضو؟`
      );

      if (sendDuplicates) {
        toast.info("جاري تحويل المتكرر إلى Girabatcom... لا تغلق الصفحة", {
          autoClose: false,
          toastId: "marking-girabatcom-dup",
        });

        const { successCount, failedCount, failedOrders } =
          await convertOrdersToGirabatcom(
            duplicateOrders.map(({ order }) => order)
          );
        totalSuccess += successCount;
        totalFailed += failedCount;

        toast.dismiss("marking-girabatcom-dup");

        if (failedOrders.length > 0) {
          markDupSkipped(failedOrders);
        }
      } else {
        markDupSkipped(duplicateOrders.map(({ order }) => order));
        toast.info(`تم تخطي ${duplicateOrders.length} طلب(ات) متكررة`);
      }
    }

    if (fincartHits.length > 0) {
      const fincartList = fincartHits
        .slice(0, 15)
        .map(
          ({ order, status }) =>
            `#${order.orderNumber} — ${order.name}` + (status ? ` (${status})` : "")
        )
        .join("\n");

      const convertExisting = window.confirm(
        `فيه ${fincartHits.length} طلب(ات) موجودين بالفعل على Fincart.\n\n${fincartList}` +
          (fincartHits.length > 15 ? "\n..." : "") +
          `\n\nمش هيتبعتوا تاني لـ Fincart.\nهل عايز تحولهم لـ Girabatcom برضو؟`
      );

      if (convertExisting) {
        await persistDetectedFincart(fincartHits);
        toast.info("جاري تحويل الموجود على Fincart إلى Girabatcom...", {
          autoClose: false,
          toastId: "marking-girabatcom-fincart",
        });

        const { successCount, failedCount, failedOrders } =
          await convertOrdersToGirabatcom(fincartHits.map((hit) => hit.order));
        totalSuccess += successCount;
        totalFailed += failedCount;
        toast.dismiss("marking-girabatcom-fincart");

        if (failedOrders.length > 0) {
          markAlreadyInFincart(failedOrders, { byId: fincartById });
        }
      } else {
        await skipFincartHits();
        toast.info(`تم تخطي ${fincartHits.length} طلب(ات) موجودين على Fincart`);
      }
    }

    setIsMarkingGirabatcom(false);

    if (
      totalSuccess === 0 &&
      duplicateOrders.length === 0 &&
      fincartHits.length === 0
    ) {
      toast.error(
        `فشل تحويل الطلبات إلى Girabatcom` +
          (totalFailed > 0 ? ` (${totalFailed} طلب)` : "")
      );
      return;
    }

    if (totalSuccess > 0) {
      toast.success(
        `تم تحويل ${totalSuccess} طلب(ات) إلى Girabatcom بنجاح!` +
          (totalFailed > 0 ? `\nفشل في ${totalFailed} طلب(ات)` : ""),
        { autoClose: 6000 }
      );
    }
  };

  const handleOrderEdited = async (updatedOrder) => {
    if (!updatedOrder?.orderId) {
      await loadInitialOrders();
      return;
    }
    try {
      const [enriched] = await enrichOrders([updatedOrder]);
      setLoadedOrders((prev) =>
        prev.map((o) =>
          o.orderNumber === enriched.orderNumber
            ? {
                ...enriched,
                dupSkipped: o.dupSkipped,
                alreadyInFincart: o.alreadyInFincart || enriched.alreadyInFincart,
                sentToFincart: o.sentToFincart || enriched.sentToFincart,
              }
            : o
        )
      );
    } catch (err) {
      console.error(err);
      await loadInitialOrders();
    }
  };

  const handleCopyOrderInfo = (order) => {
    const phone = `${order.phoneNumber}${order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""}`;
    const formattedState = `${order.state} (D)`;
    const text = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD`;
    copyToClipboard(text);
  };

  const handleCopyItems = (order) => {
    const lines = order.items.map((item, index) => {
      const itemNum = `${index + 1}-${order.items.length}`;
      const type = item.type || "Unknown Type";
      const driveLink = item.driveLink || "No Link";
      return `${itemNum}\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
    }).join('\n');
    copyToClipboard(lines);
  };

    const handleCopyAllExcelDataNoReference = async (order) => {
    try {
      // Format phone number
      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""
      }`;
      
      const formattedState = `${order.state} (D)`;
      
      // No reference field - using empty string or tab
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const itemData = teePlusPackedItemData(item);
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join('\n');
      await copyOrderToTeePlus(finalText, order.orderNumber);
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      if (err.code === "SOURCE_FAILED") toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      else toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  const isOrderCustom = (order) =>
    order.items.some((item) => item.code && item.code.startsWith("CUSTOM"));

  const isOrderNonCustom = (order) => !isOrderCustom(order);

  const filteredOrders = loadedOrders.filter((order) => {
    if (designFilter === "custom" && !isOrderCustom(order)) return false;
    if (designFilter === "normal" && !isOrderNonCustom(order)) return false;

    if (dupFilter === "clean" && order.dupSkipped) return false;
    if (dupFilter === "dup" && !order.dupSkipped) return false;

    const wentToFincart = !!(order.sentToFincart || order.alreadyInFincart);
    if (fincartFilter === "sent" && !wentToFincart) return false;
    if (fincartFilter === "notSent" && wentToFincart) return false;

    if (governorateFilter !== "all" && order.state !== governorateFilter) return false;

    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  const dupFilterCounts = {
    all: loadedOrders.length,
    clean: loadedOrders.filter((o) => !o.dupSkipped).length,
    dup: loadedOrders.filter((o) => o.dupSkipped).length,
  };

  const fincartFilterCounts = {
    all: loadedOrders.length,
    sent: loadedOrders.filter((o) => o.sentToFincart || o.alreadyInFincart).length,
    notSent: loadedOrders.filter((o) => !o.sentToFincart && !o.alreadyInFincart).length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />

      {editingOrderId && (
        <EditOrderModal
          orderId={editingOrderId}
          onClose={() => setEditingOrderId(null)}
          onSaved={handleOrderEdited}
        />
      )}

      <HoldReasonDialog
        isOpen={showHoldDialog}
        orderId={pendingHoldOrder}
        onClose={() => {
          setShowHoldDialog(false);
          setPendingHoldOrder(null);
        }}
        onConfirm={handleHoldReasonConfirm}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Printer className="text-slate-700" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-slate-700 to-cyan-600 bg-clip-text text-transparent">
              Printing Hold
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {/* Stats Cards */}
        {canViewOrders(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-slate-100 rounded-xl">
                  <Package className="text-slate-700" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص Printing Hold</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-slate-700">{printingHoldOrdersSummary.count} طلبات</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-indigo-50 rounded-xl">
                    <span className="text-gray-600">عدد القطع</span>
                    <span className="text-2xl font-bold text-indigo-600">{printingHoldOrdersSummary.totalProductsSold ?? 0} قطعة</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{printingHoldOrdersSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-slate-300 focus:border-slate-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-6 py-3 border-2 rounded-xl transition-all ${
                showFilters || designFilter !== "all" || dupFilter !== "all" || fincartFilter !== "all" || governorateFilter !== "all"
                  ? "bg-slate-50 border-slate-400 text-slate-700"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Filter size={20} />
              تصفية
              {(designFilter !== "all" || dupFilter !== "all" || fincartFilter !== "all" || governorateFilter !== "all") && (
                <span className="bg-slate-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {(designFilter !== "all" ? 1 : 0) + (dupFilter !== "all" ? 1 : 0) + (fincartFilter !== "all" ? 1 : 0) + (governorateFilter !== "all" ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <span className="text-sm text-gray-600 ml-2">المحافظة:</span>
                <select
                  value={governorateFilter}
                  onChange={(e) => setGovernorateFilter(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-slate-300 focus:border-slate-400 transition-all bg-white text-sm font-medium min-w-[200px]"
                >
                  <option value="all">كل المحافظات</option>
                  {EGYPTIAN_GOVERNORATES.map((gov) => (
                    <option key={gov} value={gov}>
                      {gov}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <span className="text-sm text-gray-600 ml-2">Fincart:</span>
                {[
                  { key: "all", label: "الكل", count: fincartFilterCounts.all, active: "bg-indigo-600 text-white border-indigo-600" },
                  { key: "notSent", label: "مراحش Fincart", count: fincartFilterCounts.notSent, active: "bg-gray-700 text-white border-gray-700" },
                  { key: "sent", label: "راح Fincart", count: fincartFilterCounts.sent, active: "bg-orange-500 text-white border-orange-500" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setFincartFilter(opt.key)}
                    className={`px-4 py-2 rounded-xl border-2 text-sm font-bold transition-all ${
                      fincartFilter === opt.key
                        ? opt.active
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label} ({opt.count})
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <span className="text-sm text-gray-600 ml-2">التكرار:</span>
                {[
                  { key: "all", label: "الكل", count: dupFilterCounts.all, active: "bg-indigo-600 text-white border-indigo-600" },
                  { key: "clean", label: "غير متكرر", count: dupFilterCounts.clean, active: "bg-gray-700 text-white border-gray-700" },
                  { key: "dup", label: "DUP", count: dupFilterCounts.dup, active: "bg-red-600 text-white border-red-600" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setDupFilter(opt.key)}
                    className={`px-4 py-2 rounded-xl border-2 text-sm font-bold transition-all ${
                      dupFilter === opt.key
                        ? opt.active
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label} ({opt.count})
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <span className="text-sm text-gray-600 ml-2">نوع التصميم:</span>
                {[
                  { value: "all", label: "كل الطلبات" },
                  { value: "custom", label: "طلبات مخصصة (CUSTOM)" },
                  { value: "normal", label: "طلبات عادية" },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setDesignFilter(value)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      designFilter === value
                        ? value === "custom"
                          ? "bg-green-600 text-white shadow-md"
                          : value === "normal"
                            ? "bg-gray-600 text-white shadow-md"
                            : "bg-slate-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <label className="font-medium text-gray-700">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-slate-300 focus:border-slate-400 transition-all bg-white"
                disabled={isOrdersLoading}
              >
                <option value={10}>10 طلبات</option>
                <option value={20}>20 طلب</option>
                <option value={30}>30 طلب</option>
                <option value={40}>40 طلب</option>
                <option value={50}>50 طلب</option>
                <option value={80}>80 طلب</option>
                <option value={100}>100 طلب</option>
              </select>
              <span className="text-2xl text-gray-600">
                (المعروض: {filteredOrders.length} / المحمّل: {loadedOrders.length})
              </span>
            </div>
            {isOrdersLoading && (
              <div className="flex items-center gap-2 text-slate-700">
                <Loader className="animate-spin" size={20} />
                <span>جاري التحميل...</span>
              </div>
            )}
          </div>
        </div>

        {/* Bulk actions */}
        {canAct && (
          <div className="bg-gradient-to-r from-cyan-50 to-slate-50 backdrop-blur-sm rounded-2xl shadow-xl p-4 md:p-6 mb-6 border-2 border-cyan-300">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-cyan-100 rounded-xl">
                  <Building2 className="text-cyan-700" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-cyan-900 text-lg">تحويل المحدد إلى Girabatcom</h3>
                  <p className="text-sm text-cyan-700">
                    المحدد: <span className="font-bold">{selectedOrderNumbers.size}</span> طلب
                    {" · "}فحص التكرار و Fincart قبل التحويل
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSelectAllVisible(true, filteredOrders)}
                  disabled={filteredOrders.length === 0 || isMarkingGirabatcom}
                  className="bg-white border-2 border-cyan-400 text-cyan-800 font-bold px-5 py-3 rounded-xl hover:bg-cyan-50 disabled:opacity-50 transition-all flex items-center gap-2"
                >
                  <CheckSquare size={18} />
                  تحديد الكل
                </button>
                <button
                  type="button"
                  onClick={() => setSelectAllVisible(false, filteredOrders)}
                  disabled={selectedOrderNumbers.size === 0 || isMarkingGirabatcom}
                  className="bg-white border-2 border-gray-300 text-gray-700 font-medium px-5 py-3 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all"
                >
                  إلغاء التحديد
                </button>
                <button
                  type="button"
                  onClick={handleMarkSelectedAsGirabatcom}
                  disabled={
                    isOrdersLoading ||
                    selectedOrderNumbers.size === 0 ||
                    isMarkingGirabatcom
                  }
                  className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-bold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isMarkingGirabatcom ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      جاري التحويل...
                    </>
                  ) : (
                    <>
                      <Building2 size={18} />
                      تحويل لـ Girabatcom ({selectedOrderNumbers.size})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Orders Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  <table className="w-full min-w-[1200px] table-auto">
                    <thead>
                      <tr className="bg-gradient-to-r from-slate-50 to-cyan-50 border-b-2 border-slate-200">
                        <th className="p-2 text-center font-bold text-slate-800">
                          <div className="flex flex-col items-center gap-1">
                            <input
                              type="checkbox"
                              className="w-5 h-5 accent-cyan-600"
                              checked={
                                filteredOrders.length > 0 &&
                                filteredOrders.every((o) =>
                                  selectedOrderNumbers.has(o.orderNumber)
                                )
                              }
                              onChange={(e) =>
                                setSelectAllVisible(e.target.checked, filteredOrders)
                              }
                            />
                            <span className="text-xs">الطلب</span>
                          </div>
                        </th>
                        <th className="p-4 text-right font-bold text-slate-800">العميل</th>
                        <th className="p-4 text-right font-bold text-slate-800">العنوان</th>
                        <th className="p-4 text-center font-bold text-slate-800">الإجمالي</th>
                        <th className="p-4 text-right font-bold text-slate-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-slate-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-slate-800">نسخ</th>
                        <th className="p-4 text-center font-bold text-slate-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-slate-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                          {/* Order Number + Checkbox */}
                          <td className="p-2 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <input
                                type="checkbox"
                                className="w-5 h-5 accent-cyan-600"
                                checked={selectedOrderNumbers.has(order.orderNumber)}
                                onChange={() => toggleSelectOrder(order.orderNumber)}
                              />
                              <span className="font-mono font-bold text-slate-700 text-sm">#{order.orderNumber}</span>
                              {order.alreadyInFincart && (
                                <span
                                  title="الأوردر موجود بالفعل على Fincart"
                                  className="text-[10px] font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5 flex items-center gap-0.5"
                                >
                                  <Truck size={10} />
                                  old in fincart
                                </span>
                              )}
                              {order.dupSkipped && (
                                <span
                                  title="تم تخطيه بسبب تكرار الرقم"
                                  className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                                >
                                  DUP
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Customer Name */}
                          <td className="p-4">
                            {isAdmin(authState.role) && (
                                <button
                                  onClick={() => setEditingOrderId(order.orderNumber)}
                                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-all mb-5"
                                >
                                  <div className="flex items-center justify-center gap-1">
                                    <Edit size={14} />
                                    تعديل
                                  </div>
                                </button>
                            )}
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span className="font-medium">{order.name}</span>
                            </div>

                          {/* Phone Numbers */}
                          <br />
                          <div className="flex items-center gap-2">
                          <Phone size={16} className="text-green-600" />

                          <div className="flex flex-col">
                            <span
                              onClick={() => navigator.clipboard.writeText(order.phoneNumber)}
                              className="text-green-600 hover:underline cursor-pointer"
                              title="Copy phone number"
                            >
                              {order.phoneNumber}
                            </span>

                            {order.phoneNumber2 !== "غير معروف" && (
                              <span
                                onClick={() => navigator.clipboard.writeText(order.phoneNumber2)}
                                className="text-green-600 hover:underline cursor-pointer"
                                title="Copy phone number"
                              >
                                {order.phoneNumber2}
                              </span>
                            )}
                          </div>
                        </div>
                          </td>

                          {/* Address */}
                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-start gap-2">
                              <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                              <span style={{fontFamily : "cairo black"}} className="text-[13px] line-clamp-5">{order.address}</span>
                            </div>
                            <br />
                             <div className="flex items-center gap-2">
                              <Map size={16} className="text-gray-500" />
                              <span className="text-2xl text-slate-700">{order.state}</span>
                            </div>
                          </td>

                         

                          {/* Total */}
                          <td className="p-4 text-center">
                            <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                          </td>

                          {/* Products + Drive Links - Merged */}
                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="mb-3">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <div className="font-medium text-slate-700">{item.name}</div>
                                    <div style={{fontFamily:"cairo black"}} className="text-[13px] text-black-600">{item.type}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      {item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-green-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                       {!item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                      
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-slate-600 hover:text-slate-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          
                                          {item.code.startsWith("CUSTOM") && (
                                            <button
                                              onClick={() =>
                                                handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)
                                              }
                                              className="flex items-center gap-2 text-white hover:text-white transition-transform hover:scale-110 px-3 py-2"
                                              title="تحميل الصورة"
                                              style={{
                                                backgroundColor: "#15d225",
                                                borderRadius: "4px",
                                              }}
                                            >
                                              <Download size={16} />
                                              <span className="text-sm">Download</span>
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {index < order.items.length - 1 && (
                                  <hr className="my-2 border-gray-200" />
                                )}
                              </div>
                            ))}
                          </td>

                          {/* Notes */}
                          <td className="p-4 max-w-[150px]">
                            <p style={{fontFamily:"cairo black"}} className="text-[16px]">{order.note || "—"}</p>
                          </td>

                          {/* Copy Buttons */}
                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleCopyOrderInfo(order)}
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={14} />
                                العميل
                              </button>
                              <button
                                onClick={() => handleCopyItems(order)}
                                className="bg-gradient-to-r from-slate-500 to-cyan-500 text-white px-6 py-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-bold flex items-center justify-center gap-2"
                              >
                                <Layers size={14} />
                                المنتجات
                              </button>
                              <button
                                onClick={() => handleCopyAllExcelDataNoReference(order)}
                                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={16} />
                                نسخ (TeePlus)
                              </button>
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="p-2">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Confirmed")}
                                className="bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <CheckCircle size={14} />
                                  ارجاع الطلب اي مؤكد
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                className="bg-yellow-600 text-white px-6 py-4 rounded-lg hover:bg-yellow-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Clock size={14} />
                                  معلق
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                                className="bg-gray-600 text-white px-6 py-4 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Girabatcom")}
                                className="bg-cyan-500 text-white px-6 py-4 rounded-lg hover:bg-cyan-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Building2 size={14} />
                                  Girabatcom
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Incomplete")}
                                className="bg-amber-600 text-white px-6 py-4 rounded-lg hover:bg-amber-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Incomplete
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>

                          {/* Delete */}
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-slate-700 to-cyan-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <Printer className="mx-auto mb-4 text-gray-400" size={64} />
                  {loadedOrders.length > 0 ? (
                    <>
                      <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات تطابق التصفية</p>
                      <p className="text-lg text-gray-500">جرّب تغيير نوع التصميم أو البحث</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات في Printing Hold</p>
                      <p className="text-lg text-gray-500">سيتم عرض الطلبات المعلّقة للطباعة هنا</p>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintingHoldOrdersPage;